import type { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { DetailsModule as DetailsModuleValue } from "@reactivated";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/components/pdp-details.html`` (the tempurpedic-local PDP
 * ``DetailsModule``, ``PDPModuleAssembly`` key ``details``).
 *
 * The accordion open/close interactivity is owned by this component's own
 * state (the AccordionBlock precedent): a click on the ``.accordion`` button
 * toggles ``accordion--is-active`` on the trigger, ``accordion-target--is-active``
 * on the ``#pdp-more-details`` panel, and their ``aria-expanded`` /
 * ``aria-hidden`` flags.
 *
 * ``show_columns`` drives the *initial* open/closed state exactly as the legacy
 * ``{% if value.show_columns %}`` did: the ``accordion--is-active`` /
 * ``accordion-target--is-active`` classes and the button ``aria-expanded``
 * value. It defaults to ``True`` server-side but serializes as
 * ``boolean | null``; ``null`` / ``false`` => collapsed (legacy falsy parity).
 *
 * Each column item's ``copy`` is a ``RichTextBlock`` VALUE. Legacy
 * ``{{ detail.copy|safe }}`` calls ``str(RichText)`` -> ``RichText.__html__`` ->
 * the ``wagtailcore/shared/richtext.html`` template, which (with
 * ``wagtail.contrib.legacy.richtext`` installed) wraps the expanded HTML in
 * ``<div class="rich-text">``. So legacy emitted
 * ``.pdp-details__copy > .rich-text > EXPANDED`` -- and the global ``.rich-text``
 * SCSS styles editor-applied classes (``.razzmatazz``/``.light``/colors) as
 * ``.rich-text`` descendants. The serializer ships BARE expanded HTML
 * (``_bare_richtext`` -> ``expand_db_html``); this component re-adds the single
 * ``.rich-text`` wrapper (validated OffersBlock pattern) so the load-bearing
 * wrapper is preserved. ``title`` is plain CharBlock text (React auto-escapes
 * it, matching ``{{ detail.title }}``).
 *
 * The legacy class strings carry literal surrounding whitespace
 * (`` accordion--is-active ``) from the ``{% if %}`` blocks; reproduced verbatim
 * so the served markup is byte-equivalent.
 */
type Value = DetailsModuleValue;
type DetailItem = NonNullable<Value["column1"]>[number];

const DetailColumn = (
    props: Immutable<{ items: readonly DetailItem[] | null | undefined }>,
): React.JSX.Element => {
    const items = props.items ?? [];
    return (
        <div className="pdp-section__column">
            {items.map((detail, i) => (
                <div className="pdp-section__block" key={i}>
                    <h3 className="pdp-section__title pdp-section__title--small">
                        {detail.title}
                    </h3>
                    <div className="pdp-section__body pdp-details__copy">
                        <div
                            className="rich-text"
                            dangerouslySetInnerHTML={{
                                __html: detail.copy ?? "",
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

export const DetailsModule = (
    props: Immutable<{ value: DetailsModuleValue }>,
): React.JSX.Element => {
    const v = props.value;
    const [isExpanded, setIsExpanded] = React.useState(!!v.show_columns);
    return (
        <div className="pdp-section pdp-details l-capped-width">
            <h3 className="pdp-details__heading">
                <button
                    type="button"
                    className={
                        isExpanded
                            ? "pdp-details__title pdp-section__title accordion  accordion--is-active "
                            : "pdp-details__title pdp-section__title accordion "
                    }
                    aria-controls="pdp-more-details"
                    aria-expanded={isExpanded ? "true" : "false"}
                    onClick={(event) => {
                        event.preventDefault();
                        setIsExpanded((value) => !value);
                    }}
                >
                    {t`More Details`}
                    <span
                        className="pdp-details__trigger-icon"
                        aria-label="Toggle icon"
                    />
                </button>
            </h3>

            <div
                className={
                    isExpanded
                        ? "pdp-details__columns accordion-target  accordion-target--is-active "
                        : "pdp-details__columns accordion-target "
                }
                id="pdp-more-details"
                aria-hidden={isExpanded ? "false" : "true"}
            >
                <DetailColumn items={v.column1} />
                <DetailColumn items={v.column2} />
                <DetailColumn items={v.column3} />
            </div>
        </div>
    );
};
